import { useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2 } from "lucide-react";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

export const Route = createFileRoute("/_authenticated/admin/orgs/$id")({
  component: AdminOrgDetailPage,
});

const addMemberSchema = z.object({
  email: z.string().email("Nieprawidłowy email"),
  role: z.enum(["owner", "member"]),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
});

type AddMemberForm = z.infer<typeof addMemberSchema>;

function AdminOrgDetailPage() {
  const { id } = Route.useParams();
  const [dialogOpen, setDialogOpen] = useState(false);
  const utils = trpc.useUtils();

  const { data: org, isLoading } = trpc.admin.getAdminOrg.useQuery({
    orgId: id,
  });

  const addMemberMutation = trpc.admin.addOrgMember.useMutation({
    onSuccess: () => {
      toast.success("Członek dodany");
      utils.admin.getAdminOrg.invalidate({ orgId: id });
      utils.admin.listAdminOrgs.invalidate();
      setDialogOpen(false);
      form.reset();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const removeMemberMutation = trpc.admin.removeOrgMember.useMutation({
    onSuccess: () => {
      toast.success("Członek usunięty");
      utils.admin.getAdminOrg.invalidate({ orgId: id });
      utils.admin.listAdminOrgs.invalidate();
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const form = useForm<AddMemberForm>({
    resolver: zodResolver(addMemberSchema),
    defaultValues: { email: "", role: "member", firstName: "", lastName: "" },
  });

  const onSubmit = (data: AddMemberForm) => {
    addMemberMutation.mutate({
      orgId: id,
      email: data.email,
      role: data.role,
      firstName: data.firstName || null,
      lastName: data.lastName || null,
    });
  };

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  if (!org) {
    return (
      <p className="text-sm text-destructive">Organizacja nie znaleziona</p>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/admin/orgs">
          <Button variant="ghost" size="sm">
            <ArrowLeft className="mr-1 size-4" />
            Powrót
          </Button>
        </Link>
        <div>
          <h1 className="text-2xl font-bold">{org.name}</h1>
          {org.contactEmail && (
            <p className="text-sm text-muted-foreground">{org.contactEmail}</p>
          )}
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          Członkowie ({org.members.length})
        </h2>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="mr-2 size-4" />
              Dodaj członka
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Dodaj członka</DialogTitle>
            </DialogHeader>
            <DialogForm onSubmit={form.handleSubmit(onSubmit)}>
              <DialogBody className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    {...form.register("email")}
                    placeholder="user@example.com"
                  />
                  {form.formState.errors.email && (
                    <p className="text-xs text-destructive">
                      {form.formState.errors.email.message}
                    </p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="mem-first">Imię</Label>
                    <Input
                      id="mem-first"
                      {...form.register("firstName")}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mem-last">Nazwisko</Label>
                    <Input
                      id="mem-last"
                      {...form.register("lastName")}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Rola</Label>
                  <Controller
                    control={form.control}
                    name="role"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="member">Członek</SelectItem>
                          <SelectItem value="owner">Właściciel</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
              </DialogBody>
              <DialogFooter>
                <Button type="submit" disabled={addMemberMutation.isPending}>
                  {addMemberMutation.isPending ? "Dodawanie..." : "Dodaj"}
                </Button>
              </DialogFooter>
            </DialogForm>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-muted-foreground">
                <th className="px-4 py-3 font-medium">Użytkownik</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Rola</th>
                <th className="px-4 py-3 font-medium">Dołączył</th>
                <th className="px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {org.members.map((member) => (
                <tr key={member.id} className="border-b last:border-0">
                  <td className="px-4 py-3 font-medium">
                    {member.firstName ?? ""} {member.lastName ?? ""}
                    {!member.firstName && !member.lastName && (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {member.email}
                  </td>
                  <td className="px-4 py-3">
                    <Badge
                      variant={
                        member.role === "owner" ? "default" : "secondary"
                      }
                    >
                      {member.role === "owner" ? "Właściciel" : "Członek"}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(member.joinedAt).toLocaleDateString("pl-PL")}
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (
                          confirm(
                            `Usunąć ${member.email} z organizacji?`,
                          )
                        ) {
                          removeMemberMutation.mutate({
                            orgId: id,
                            userId: member.userId,
                          });
                        }
                      }}
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </td>
                </tr>
              ))}
              {org.members.length === 0 && (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-8 text-center text-muted-foreground"
                  >
                    Brak członków
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
